import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../supabaseClient';
import { chatService } from '../../services/chatService';
import { userService } from '../../services/userService';
import ChatFloatingPanel from './ChatFloatingPanel';
import ChatWindow from './ChatWindow';
import { Box } from '@mui/material';

// Notification sound (simple beep)
const playNotificationSound = () => {
  try {
    const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3'); // Simple notification sound
    audio.volume = 0.5;
    audio.play().catch(e => console.log("Audio play failed (interaction required)", e));
  } catch (e) {
    console.error("Audio error", e);
  }
};

const ChatManager = ({ currentUser }) => {
  const [allUsers, setAllUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [openChats, setOpenChats] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const presenceChannelRef = useRef(null);
  const messageChannelRef = useRef(null);
  const openChatsRef = useRef([]);
  const allUsersRef = useRef([]);
  const [minimizedChats, setMinimizedChats] = useState([]);
  const minimizedChatsRef = useRef([]);
  const normalizeUserId = (id) => String(id);

  useEffect(() => {
    if (!currentUser) return;

    // 1. Fetch all users for the directory
    loadUsers();

    // 2. Subscribe to Presence
    const presenceChannel = chatService.subscribeToPresence(
      currentUser.userid,
      {
        userid: currentUser.userid,
        firstname: currentUser.firstname,
        lastname: currentUser.lastname,
        online_at: new Date().toISOString()
      },
      (users) => {
        setOnlineUsers(users);
      }
    );
    presenceChannelRef.current = presenceChannel;

    // 3. Subscribe to ALL incoming messages for notifications/unread counts
    const messageChannel = chatService.subscribeToMessages(currentUser.userid, (msg) => {
      const senderId = msg.sender_id;
      const isOpen = openChatsRef.current.some(chat => chat.userid === senderId);
      const senderUser = allUsersRef.current.find(u => u.userid === senderId) || { userid: senderId, firstname: '', lastname: '' };
      if (!isOpen) {
        // If minimized, restore it
        const wasMinimized = minimizedChatsRef.current.some(u => u.userid === senderId);
        if (wasMinimized) {
          setMinimizedChats(prev => prev.filter(u => u.userid !== senderId));
        }
        setOpenChats(prev => {
          if (prev.some(u => u.userid === senderId)) return prev;
          if (prev.length >= 3) {
            const [_, ...rest] = prev;
            return [...rest, senderUser];
          }
          return [...prev, senderUser];
        });
      }
      setUnreadCounts(prev => ({
        ...prev,
        [senderId]: (prev[senderId] || 0) + 1
      }));
      playNotificationSound();
    });
    messageChannelRef.current = messageChannel;

    (async () => {
      const res = await chatService.getUnreadBySender(currentUser.userid);
      if (res.success) {
        const map = {};
        res.data.forEach(m => {
          map[m.sender_id] = (map[m.sender_id] || 0) + 1;
        });
        setUnreadCounts(map);
      }
    })();

    return () => {
      if (presenceChannelRef.current) supabase.removeChannel(presenceChannelRef.current);
      if (messageChannelRef.current) supabase.removeChannel(messageChannelRef.current);
    };
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const key = `chat_open_${currentUser.userid}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const items = JSON.parse(saved);
        if (Array.isArray(items)) {
          const restored = items
            .map(item => {
              if (typeof item === 'string' || typeof item === 'number') {
                return { userid: String(item), firstname: '', lastname: '' };
              }
              return { userid: item.userid, firstname: item.firstname || '', lastname: item.lastname || '' };
            })
            .filter(it => it && it.userid);
          if (restored.length > 0) setOpenChats(restored);
        }
      } catch {}
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const key = `chat_open_${currentUser.userid}`;
    const minimal = openChats.map(u => ({ userid: u.userid, firstname: u.firstname || '', lastname: u.lastname || '' }));
    try {
      localStorage.setItem(key, JSON.stringify(minimal));
    } catch {}
  }, [openChats, currentUser]);
  
  useEffect(() => {
    openChatsRef.current = openChats;
  }, [openChats]);
  
  useEffect(() => {
    allUsersRef.current = allUsers;
  }, [allUsers]);
  
  useEffect(() => {
    if (!currentUser) return;
    const key = `chat_min_${currentUser.userid}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const items = JSON.parse(saved);
        if (Array.isArray(items)) {
          const restored = items
            .map(item => {
              if (typeof item === 'string' || typeof item === 'number') {
                return { userid: String(item), firstname: '', lastname: '' };
              }
              return { userid: item.userid, firstname: item.firstname || '', lastname: item.lastname || '' };
            })
            .filter(it => it && it.userid);
          if (restored.length > 0) setMinimizedChats(restored);
        }
      } catch {}
    }
  }, [currentUser]);
  
  useEffect(() => {
    minimizedChatsRef.current = minimizedChats;
    if (!currentUser) return;
    const key = `chat_min_${currentUser.userid}`;
    const minimal = minimizedChats.map(u => ({ userid: u.userid, firstname: u.firstname || '', lastname: u.lastname || '' }));
    try {
      localStorage.setItem(key, JSON.stringify(minimal));
    } catch {}
  }, [minimizedChats, currentUser]);
  
  useEffect(() => {
    if (allUsers.length === 0) return;
    setOpenChats(prev => prev.map(u => {
      const found = allUsers.find(x => x.userid === u.userid);
      if (!found) return u;
      return { ...u, firstname: found.firstname, lastname: found.lastname };
    }));
    setMinimizedChats(prev => prev.map(u => {
      const found = allUsers.find(x => x.userid === u.userid);
      if (!found) return u;
      return { ...u, firstname: found.firstname, lastname: found.lastname };
    }));
  }, [allUsers]);

  const loadUsers = async () => {
    const res = await userService.getAllUsers();
    if (res.success) {
      setAllUsers(res.data);
    }
  };

  const handleUserClick = (user) => {
    const userId = normalizeUserId(user.userid);
    setMinimizedChats(prev => prev.filter(u => normalizeUserId(u.userid) !== userId));
    // Open chat window if not already open
    if (!openChats.find(u => normalizeUserId(u.userid) === userId)) {
      if (openChats.length >= 3) {
        // Close oldest if too many (max 3)
        const [_, ...rest] = openChats;
        setOpenChats([...rest, { ...user, userid: userId }]);
      } else {
        setOpenChats(prev => [...prev, { ...user, userid: userId }]);
      }
    }
    
    // Clear unread count
    setUnreadCounts(prev => {
      const newCounts = { ...prev };
      delete newCounts[userId];
      return newCounts;
    });
  };

  const closeChat = (userId) => {
    const id = normalizeUserId(userId);
    setOpenChats(prev => prev.filter(u => normalizeUserId(u.userid) !== id));
    setMinimizedChats(prev => prev.filter(u => normalizeUserId(u.userid) !== id));
  };

  const minimizeChat = (userId) => {
    const id = normalizeUserId(userId);
    const userToMinimize = openChats.find(u => normalizeUserId(u.userid) === id);

    setOpenChats(prev => prev.filter(u => normalizeUserId(u.userid) !== id));
    const existing = minimizedChatsRef.current.some(u => normalizeUserId(u.userid) === id);
    if (!existing) {
      const user = userToMinimize || allUsersRef.current.find(u => normalizeUserId(u.userid) === id) || { userid: id, firstname: '', lastname: '' };
      setMinimizedChats(prev => [...prev, user]);
    }
  };
  
  const restoreChat = (userId) => {
    const id = normalizeUserId(userId);
    const userToRestore = minimizedChats.find(u => normalizeUserId(u.userid) === id);
    setMinimizedChats(prev => prev.filter(u => normalizeUserId(u.userid) !== id));
    
    const user = userToRestore || allUsersRef.current.find(u => normalizeUserId(u.userid) === id) || { userid: id, firstname: '', lastname: '' };
    setOpenChats(prev => {
      if (prev.some(u => normalizeUserId(u.userid) === id)) return prev;
      if (prev.length >= 3) {
        const [_, ...rest] = prev;
        return [...rest, user];
      }
      return [...prev, user];
    });
    setUnreadCounts(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  if (!currentUser) return null;
 
  const overlay = (
    <Box sx={{ position: 'fixed', bottom: 0, right: 0, zIndex: 2147483000 }}>
      <Box>
        <ChatFloatingPanel 
          currentUser={currentUser}
          onlineUsers={onlineUsers}
          allUsers={allUsers}
          unreadCounts={unreadCounts}
          onUserClick={handleUserClick}
        />
      </Box>

      {/* Chat Windows */}
      <Box sx={{ 
        position: 'fixed', 
        bottom: 0, 
        right: { xs: 12, md: 360 },
        display: 'flex', 
        gap: 2,
        flexDirection: 'row-reverse',
        alignItems: 'flex-end',
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' }
      }}>
        {openChats.map((user, index) => (
          <Box key={user.userid}>
            <ChatWindow 
              currentUser={currentUser}
              recipient={{ ...user, isOnline: onlineUsers.some(u => u.userid === user.userid) }}
              onClose={() => closeChat(user.userid)}
              onMinimize={() => minimizeChat(user.userid)}
              position={index}
              unread={unreadCounts[user.userid] || 0}
              onRead={() => {
                setUnreadCounts(prev => {
                  const next = { ...prev };
                  delete next[user.userid];
                  return next;
                });
              }}
            />
          </Box>
        ))}
      </Box>
      
      {/* Minimized Tabs */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        right: { xs: 12, md: 360 },
        display: 'flex',
        gap: 1,
        flexDirection: 'row-reverse',
        alignItems: 'flex-end'
      }}>
        {minimizedChats.map((u) => (
          <Box key={`min-${u.userid}`}>
            <Box
              onClick={() => restoreChat(u.userid)}
              sx={{
                bgcolor: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '10px 10px 0 0',
                height: 38,
                minWidth: 160,
                px: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: '#ecfdf5',
                  borderColor: '#059669'
                }
              }}
            >
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#059669', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
                {u.firstname?.[0]}{u.lastname?.[0]}
              </Box>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Box sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  {u.firstname} {u.lastname}
                </Box>
              </Box>
              <Box>
                {unreadCounts[u.userid] > 0 && (
                  <Box sx={{ bgcolor: '#ef4444', color: 'white', borderRadius: '50%', width: 18, height: 18, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {unreadCounts[u.userid]}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
  
  return createPortal(overlay, document.body);
};

export default ChatManager;
